// Drives the Google Website Translate widget (loaded in public/index.html) so the
// entire app — every page, including hardcoded English content — translates in place.
//
// IMPORTANT: We translate by driving the widget's hidden <select> (.goog-te-combo)
// directly, WITHOUT reloading the page. The old cookie + reload approach failed
// inside cross-origin iframes (Emergent workspace preview) and on iOS Safari, where
// the `googtrans` cookie gets partitioned/dropped on reload, so the page came back
// in English. localStorage is used to remember the choice and re-apply it on load.

const STORAGE_KEY = 'siteLang';

function writeCookie(name, value) {
  const d = new Date();
  d.setTime(d.getTime() + 365 * 24 * 60 * 60 * 1000);
  const expires = `;expires=${d.toUTCString()}`;
  const host = window.location.hostname;
  document.cookie = `${name}=${value}${expires};path=/`;
  document.cookie = `${name}=${value}${expires};path=/;domain=${host}`;
  const parts = host.split('.');
  if (parts.length > 2) {
    document.cookie = `${name}=${value}${expires};path=/;domain=.${parts.slice(-2).join('.')}`;
  }
}

function eraseCookie(name) {
  const past = ';expires=Thu, 01 Jan 1970 00:00:00 UTC';
  const host = window.location.hostname;
  document.cookie = `${name}=${past};path=/`;
  document.cookie = `${name}=${past};path=/;domain=${host}`;
  const parts = host.split('.');
  if (parts.length > 2) {
    document.cookie = `${name}=${past};path=/;domain=.${parts.slice(-2).join('.')}`;
  }
}

// Returns the active target language ('en' when not translating).
export function getTranslateLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch (e) {
    /* localStorage may be blocked */
  }
  const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (m) {
    try {
      const parts = decodeURIComponent(m[1]).split('/'); // e.g. /en/es
      return parts[2] || 'en';
    } catch (e) {
      /* ignore */
    }
  }
  return 'en';
}

// Repeatedly try to drive the Google Translate <select> until the widget exists,
// then apply the target language. Reload-free — this is what makes it reliable
// inside iframes and on mobile Safari.
function driveCombo(lang, tries = 60) {
  const combo = document.querySelector('.goog-te-combo');
  if (combo) {
    if (combo.value !== lang) {
      combo.value = lang;
      combo.dispatchEvent(new Event('change'));
    }
    return;
  }
  if (tries > 0) {
    setTimeout(() => driveCombo(lang, tries - 1), 250);
  }
}

// Sets the whole-app translation language.
export function setTranslateLang(lang) {
  const target = lang || 'en';
  if (getTranslateLang() === target) return;

  if (target === 'en') {
    // Reverting to the source language: the widget can't cleanly "un-translate"
    // via the select, so a clean reload with no cookie restores original English.
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    eraseCookie('googtrans');
    window.location.reload();
    return;
  }

  try { localStorage.setItem(STORAGE_KEY, target); } catch (e) { /* ignore */ }
  writeCookie('googtrans', `/en/${target}`); // best-effort persistence for full reloads
  driveCombo(target); // immediate, reload-free translation
}

// Re-apply the saved language on every fresh page load. Covers the case where the
// googtrans cookie was dropped (cross-origin iframe / iOS Safari) by re-driving the
// widget from localStorage once it has loaded.
export function initTranslate() {
  const lang = getTranslateLang();
  if (lang && lang !== 'en') {
    writeCookie('googtrans', `/en/${lang}`);
    driveCombo(lang);
  }
}
